/**
 * Referral Gate Modal
 * Blocks site access if user doesn't have a referral code or existing access
 */

import React, { useState } from 'react';
import { useReferral } from '../../contexts/ReferralContext';
import { useWallet } from '../../contexts/WalletProvider';
import { useGameSession } from '../../contexts/GameSessionProvider';
import ResultsTicker from '../BettingContainer/ResultsTicker';
import './Modal.css';
import './ReferralGateModal.css';

interface ReferralGateModalProps {
  onClose?: () => void;
}

const ReferralGateModal: React.FC<ReferralGateModalProps> = ({ onClose }) => {
  const { applyReferralCode, loading, pendingCode } = useReferral();
  const { address: wallet, login } = useWallet();
  const gameSession = useGameSession();
  
  const [codeInput, setCodeInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  const handleApplyCode = async () => {
    const code = codeInput.trim().toUpperCase();
    
    if (!code) {
      setError('Please enter a referral code');
      return;
    }
    
    setError(null);
    
    // Store the code (like it was in the URL) and close gate
    // It will auto-apply when wallet connects
    sessionStorage.setItem('pendingReferralCode', code);
    
    // Trigger a state update to recognize the pending code
    window.dispatchEvent(new Event('storage'));
    
    // Close the gate - user can now browse
    if (onClose) onClose();
    
    // If wallet is already connected, apply immediately
    if (wallet) {
      const result = await applyReferralCode(code);
      if (!result.success) {
        setError(result.error || 'Invalid referral code');
      }
    }
  };
  
  const handleConnectWallet = async () => {
    try {
      login();
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      setError('Failed to connect wallet');
    }
  };
  
  const tickerResults = gameSession?.recentResults || [];  
  return (
    <div className="referral-gate-container">
      <div className="modal-overlay referral-gate-overlay">
        <div className="referral-gate-modal-wrapper">
          {/* Activity Ticker - positioned relative to modal */}
          <div className="referral-ticker-wrapper">
            <ResultsTicker results={tickerResults} />
          </div>
          
          <div className="modal-content referral-gate-modal">
            {/* Scanline effect overlay */}
            <div className="referral-scanlines" />
            
            {/* Feen Logo */}
            <div className="referral-gate-logo">
              <img 
                src="/assets/icons/feen-1000x.svg" 
                alt="Feen"
              />
            </div>
            
            <div className="referral-gate-body">
              {pendingCode ? (
                // Have code from URL - just need to connect wallet
                <>
                  <div className="referral-code-display">
                <span className="referral-code-label">YOUR INVITE CODE</span>
                <span className="referral-code-value">{pendingCode}</span>
              </div>
              
              {!wallet ? (
                <div className="referral-gate-action">
                  <p className="connect-prompt">Connect your wallet to enter</p>
                  <button 
                    className="referral-primary-button"
                    onClick={handleConnectWallet}
                    disabled={loading.applyingCode}
                  >
                    <span className="button-glow" />
                    CONNECT WALLET
                  </button>
                </div>
              ) : (
                <div className="referral-gate-loading">
                  <div className="spinner-glow">
                    <div className="spinner" />
                  </div>
                  <p>Activating access...</p>
                </div>
              )}
            </>
          ) : (
            // No code - need to enter one
            <>
              <div className="referral-gate-input-section">
                <div className="input-button-row">
                  <div className="input-wrapper">
                    <input
                      id="referral-code-input"
                      type="text"
                      className="referral-code-input"
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                      placeholder="INVITE CODE"
                      maxLength={10}
                      disabled={loading.applyingCode}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && codeInput.trim()) {
                          handleApplyCode();
                        }
                      }}
                    />
                    <div className="input-glow" />
                  </div>
                  <button 
                    className="referral-primary-button"
                    onClick={handleApplyCode}
                    disabled={loading.applyingCode || !codeInput.trim()}
                  >
                    <span className="button-glow" />
                    {loading.applyingCode ? 'VERIFYING...' : 'SUBMIT'}
                  </button>
                </div>
              </div>
              
              {error && (
                <div className="referral-gate-error">
                  <span className="error-icon">⚠</span>
                  {error}
                </div>
              )}
              
              <div className="referral-gate-footer">
                <p className="footer-main">Don't have a code?</p>
                <p className="referral-help-text">
                  Ask a friend who's already playing for their referral link, or{' '}
                  <a 
                    href="https://twitter.com/feentime" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="twitter-link-inline"
                  >
                    follow us on X
                  </a>
                  {' '}to be notified when we share more codes
                </p>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralGateModal;

